import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
  useUpdateNodeInternals,
} from "reactflow";
import { Boxes, FlaskConical, Plus, Trash2 } from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import { executeWorkflow } from "../createPluginApis";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";

interface FieldMapping {
  id: string;
  field1: string;
  field2: string;
}

interface MergeNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  mode?: string;
  output?: number;
  combineBy?: string;
  clashHandling?: string;
  includeAllFields?: boolean;
  advanced?: boolean;
  fieldsToMatchString?: string;
  mergeByFields?: { values: FieldMapping[] };
  multipleMatches?: string;
  fuzzyMatching?: boolean;
  disableDotNotation?: boolean;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
  iconStyle?: any;
  storedParameters?: any;
}

const MergeNode: React.FC<NodeProps<MergeNodeData>> = ({ id, data }) => {
  const label = data.label || data.header || "Merge";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);

  // Merge-specific parameters
  const [mode, setMode] = useState<string>(data.mode || "append");
  const [numberOfInputs, setNumberOfInputs] = useState<number>(1);

  // Combine mode parameters
  const [combineBy, setCombineBy] = useState<string>(
    data.combineBy || "combineByPosition",
  );
  const [fieldNamesMatch, setFieldNamesMatch] = useState<boolean>(
    (data as any).fieldNamesMatch !== undefined
      ? (data as any).fieldNamesMatch
      : true,
  );
  const [fieldsToMatch, setFieldsToMatch] = useState<string>(
    (data as any).fieldsToMatch || "",
  );
  const [outputType, setOutputType] = useState<string>(
    (data as any).outputType || "keepMatches",
  );
  const [outputDataFrom, setOutputDataFrom] = useState<string>(
    (data as any).outputDataFrom || "both",
  );

  // Options
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [activeOptions, setActiveOptions] = useState<{
    disableDotNotation?: boolean;
    fuzzyCompare?: boolean;
  }>(() => {
    const options: { disableDotNotation?: boolean; fuzzyCompare?: boolean } =
      {};
    if ((data as any).disableDotNotation !== undefined) {
      options.disableDotNotation = (data as any).disableDotNotation;
    }
    if ((data as any).fuzzyCompare !== undefined) {
      options.fuzzyCompare = (data as any).fuzzyCompare;
    }
    return options;
  });

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Calculate the actual number of inputs to render
  // Takes the maximum of user-selected numberOfInputs and connected edges
  const actualNumberOfInputs = useMemo(() => {
    // Get max index from connected edges
    const incomingEdges = edges.filter((e) => e.target === id);
    let maxEdgeIndex = 1; // Default minimum

    if (incomingEdges.length > 0) {
      maxEdgeIndex = Math.max(
        ...incomingEdges.map((e) => {
          if (e.targetHandle) {
            const match = e.targetHandle.match(/input-(\d+)/);
            return match ? parseInt(match[1], 10) : 1;
          }
          return 1;
        }),
      );
    }

    // Return the maximum to ensure all connected edges have valid handles
    return Math.max(numberOfInputs, maxEdgeIndex);
  }, [id, edges, numberOfInputs]);

  // Calculate dynamic height for collapsed node based on number of inputs
  const collapsedHeight = useMemo(() => {
    const baseHeight = 112; // h-28 = 7rem = 112px
    const additionalHeight =
      actualNumberOfInputs > 2 ? (actualNumberOfInputs - 2) * 32 : 0;
    return baseHeight + additionalHeight;
  }, [actualNumberOfInputs]);

  // Update React Flow handle positions when number of inputs changes
  // This ensures edge drawing works correctly when handles are added/removed
  useEffect(() => {
    // Delay the update to ensure DOM has been updated
    const timer = setTimeout(() => {
      updateNodeInternals(id);
    }, 0);

    return () => clearTimeout(timer);
  }, [actualNumberOfInputs, id, updateNodeInternals]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Ref to track previous serialized updates payload to avoid redundant sync loops
  const prevSyncPayloadRef = useRef<string>("");

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync inputData from data.inputData prop (set during execution)
  // This ensures input data updates when workflow execution completes
  useEffect(() => {
    const propInputData = (data as any).inputData;

    if (!propInputData || !Array.isArray(propInputData)) {
      return;
    }

    // Deep comparison to avoid unnecessary updates
    const newDataStr = JSON.stringify(propInputData);
    const currentDataStr = JSON.stringify(inputData);

    if (newDataStr !== currentDataStr) {
      setInputData(propInputData);
    }
  }, [JSON.stringify((data as any).inputData)]);

  // Sync outputData from node data (updated during workflow/node execution)
  // Use stringified comparison to prevent re-renders when content hasn't changed
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    // Skip if no new data
    if (!newOutputData) {
      // Clear output if explicitly set to null/undefined
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    // Skip if it's the same reference (optimization)
    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    // Deep comparison - only update if stringified content changed
    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      // Content is the same, just update the reference to prevent future comparisons
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync editable state to parent so unsaved changes are tracked correctly.
  useEffect(() => {
    // Build storedParameters in backend format
    const storedParameters: any = {
      mode,
      numberOfInputs, // Always include - backend needs this to wait for all inputs
      label: label,
    };

    // Add conditional parameters based on mode
    if (mode === "append") {
      // numberOfInputs already set above
      storedParameters.output = 0; // Default output index
    }

    if (mode === "combine") {
      storedParameters.combineBy = combineBy;
      storedParameters.fieldNamesMatch = fieldNamesMatch;
      storedParameters.fieldsToMatch = fieldsToMatch;
      storedParameters.outputType = outputType;
      storedParameters.outputDataFrom = outputDataFrom;

      // Add active options
      if (activeOptions.disableDotNotation !== undefined) {
        storedParameters.disableDotNotation = activeOptions.disableDotNotation;
      }
      if (activeOptions.fuzzyCompare !== undefined) {
        storedParameters.fuzzyCompare = activeOptions.fuzzyCompare;
      }
    }

    const updates: any = {
      uid,
      title,
      description,
      mode,
      numberOfInputs,
      combineBy,
      fieldNamesMatch,
      fieldsToMatch,
      outputType,
      outputDataFrom,
      disableDotNotation: activeOptions.disableDotNotation,
      fuzzyCompare: activeOptions.fuzzyCompare,
      outputData,
      sub_type: "merge_items",
      type: "flow",
      storedParameters,
    };

    const payloadKey = JSON.stringify(updates);
    if (payloadKey === prevSyncPayloadRef.current) {
      return;
    }
    prevSyncPayloadRef.current = payloadKey;

    data.onDataChange?.(id, updates);
  }, [
    uid,
    title,
    description,
    mode,
    numberOfInputs,
    combineBy,
    fieldNamesMatch,
    fieldsToMatch,
    outputType,
    outputDataFrom,
    activeOptions,
    outputData,
    label,
    id,
  ]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) {
      const newUid = `merge_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.mode) setMode(params.mode);
    if (params.numberOfInputs !== undefined) {
      setNumberOfInputs(params.numberOfInputs);
    }
    if (params.combineBy) setCombineBy(params.combineBy);
    if (params.fieldNamesMatch !== undefined)
      setFieldNamesMatch(params.fieldNamesMatch);
    if (params.fieldsToMatch) setFieldsToMatch(params.fieldsToMatch);
    if (params.outputType) setOutputType(params.outputType);
    if (params.outputDataFrom) setOutputDataFrom(params.outputDataFrom);
    if (params.disableDotNotation !== undefined) {
      setActiveOptions((prev) => ({
        ...prev,
        disableDotNotation: params.disableDotNotation,
      }));
    }
    if (params.fuzzyCompare !== undefined) {
      setActiveOptions((prev) => ({
        ...prev,
        fuzzyCompare: params.fuzzyCompare,
      }));
    }
  }, [data.storedParameters]);

  // Helper functions for options
  const addOption = (optionName: string) => {
    setActiveOptions({ ...activeOptions, [optionName]: true });
    setShowOptionsMenu(false);
  };

  const updateOption = (optionName: string, value: boolean) => {
    setActiveOptions({ ...activeOptions, [optionName]: value });
  };

  const removeOption = (optionName: string) => {
    const newOptions = { ...activeOptions };
    delete newOptions[optionName as keyof typeof newOptions];
    setActiveOptions(newOptions);
  };

  // Delete handler
  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Execute merge operation
  const executeNode = async () => {
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Title Required",
        description: "Please enter a title for the merge node",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "No Active Mesh Flow",
        description:
          "No active mesh flow found. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    const activeMeshFlow = JSON.parse(activeMeshFlowStr);
    const pluginId = sessionStorage.getItem("editPluginId") || "";

    setLoading(true);
    setOutputData(null);
    setErrorResponse(null);

    try {
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();

      // Get execution path: only nodes from start to current node (no downstream nodes)
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Store current node reference
      const currentNode = allNodes.find((n) => n.id === id);
      if (!currentNode) {
        throw new Error("Current node not found");
      }

      // Build nodes array for workflow (including all nodes)
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current MergeNode - build from local state
          nodeType = "flow";
          subType = "merge_items";
          parameters = {
            mode,
            label: label,
          } as any;

          // Add conditional parameters based on mode
          if (mode === "append") {
            parameters.numberOfInputs = numberOfInputs;
          }

          if (mode === "combine") {
            parameters.combineBy = combineBy;
            parameters.fieldNamesMatch = fieldNamesMatch;
            parameters.fieldsToMatch = fieldsToMatch;
            parameters.outputType = outputType;
            parameters.outputDataFrom = outputDataFrom;

            // Add active options
            if (activeOptions.disableDotNotation !== undefined) {
              parameters.disableDotNotation = activeOptions.disableDotNotation;
            }
            if (activeOptions.fuzzyCompare !== undefined) {
              parameters.fuzzyCompare = activeOptions.fuzzyCompare;
            }
          }
        } else {
          // Other nodes - determine type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Override type/subtype if explicitly set in node data
          if (node.data?.sub_type) {
            subType = node.data.sub_type;
            nodeType = node.data.type || "core";
          }

          // Get parameters using centralized utility
          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.header || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes - use all nodes in execution path
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(
        executableNodes.map((n: any) => n.id),
      );

      const connections: any = {};

      // Build connections from all edges in execution path
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is an if_else node
          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // For if_else nodes, route to different output arrays based on handle
          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            // true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build workflow payload
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Merge executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Merge executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Merge execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Merge Execution Error:", error);
      setErrorResponse(error?.message || "Merge execution failed");
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Merge execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Merge execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "position",
      "nodeType",
    ];

    // Helper to unwrap json_data from backend format
    const extractJsonData = (data: any): any => {
      if (!data) return data;

      if (Array.isArray(data)) {
        return data.map((item: any) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }

      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }

      return data;
    };

    // Check for outputData first
    if (input.outputData && Object.keys(input.outputData).length > 0) {
      return extractJsonData(input.outputData);
    }

    // Check for response data
    if (input.response && Object.keys(input.response).length > 0) {
      return extractJsonData(input.response);
    }

    // Fallback to configData
    const displayData: any = {};
    if (input.configData) {
      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => (displayData[key] = value));
    }

    return displayData;
  };

  // Render input data
  const renderInputData = () => {
    if (!inputData || inputData.length === 0) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div className="mt-1">Connect nodes to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      collapsedStyle={{
        height: isExpanded
          ? "auto"
          : actualNumberOfInputs > 2
            ? `${collapsedHeight}px`
            : undefined,
      }}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles - Dynamic based on number of inputs */}
      {Array.from({ length: actualNumberOfInputs }).map((_, index) => {
        const percentage =
          actualNumberOfInputs === 1
            ? 50
            : ((index + 1) / (actualNumberOfInputs + 1)) * 100;

        return (
          <Handle
            key={`input-${index + 1}`}
            type="target"
            position={Position.Left}
            id={`input-${index + 1}`}
            style={{
              top: `${percentage}%`,
            }}
            className={handlerStyle}
          />
        );
      })}

      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Boxes
              className={`${loading || (data as any).executionStatus === "running" || (data as any).executionStatus === "pending" ? "animate-rotate-ease" : ""}`}
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
          currentHeight={collapsedHeight}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid - Modified to support resizable panels */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={true} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Configuration (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* Single scroll surface — wraps all scrollable content */}
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-3 pb-2 pr-1">
                    <div className="space-y-1">
                      <label className="text-xs">Select the Mode *</label>
                      <Select
                        value={mode}
                        onValueChange={(value) => setMode(value)}
                      >
                        <SelectTrigger className="w-full text-xs h-8">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="append" className="text-xs">
                              Append
                            </SelectItem>
                            <SelectItem value="combine" className="text-xs">
                              Combine
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Append Mode - Number of Inputs */}
                    {mode === "append" && (
                      <div className="space-y-1">
                        <label className="text-xs">
                          Select Number of Inputs
                        </label>
                        <Select
                          value={actualNumberOfInputs.toString()}
                          onValueChange={(value) =>
                            setNumberOfInputs(parseInt(value))
                          }
                        >
                          <SelectTrigger className="w-full text-xs h-8">
                            <SelectValue placeholder="Select number" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <SelectItem
                                  key={num}
                                  value={num.toString()}
                                  className="text-xs"
                                >
                                  {num}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Combine Mode - Configuration */}
                    {mode === "combine" && (
                      <div className="space-y-3">
                        {/* Combine By */}
                        <div className="space-y-1">
                          <label className="text-xs font-semibold">
                            Combine By
                          </label>
                          <Select
                            value={combineBy}
                            onValueChange={(value) => setCombineBy(value)}
                          >
                            <SelectTrigger className="w-full text-xs h-8">
                              <SelectValue placeholder="Select method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem
                                  value="combineByPosition"
                                  className="text-xs"
                                >
                                  Combine By Position
                                </SelectItem>
                                <SelectItem
                                  value="combineByFields"
                                  className="text-xs"
                                >
                                  Combine By Fields
                                </SelectItem>
                                <SelectItem
                                  value="combineAll"
                                  className="text-xs"
                                >
                                  Combine All
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Field Names Match - Checkbox */}
                        <div className="flex items-center justify-between p-2 bg-muted/20 rounded border">
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">
                              Field to Match Have Different Names
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              Enable if field names differ between inputs
                            </span>
                          </div>
                          <Switch
                            checked={!fieldNamesMatch}
                            onCheckedChange={(checked) =>
                              setFieldNamesMatch(!checked)
                            }
                            className="scale-75"
                          />
                        </div>

                        {/* Fields to Match */}
                        <div className="space-y-1">
                          <label className="text-xs font-semibold">
                            Fields to Match
                          </label>
                          <Input
                            value={fieldsToMatch}
                            onChange={(e) => setFieldsToMatch(e.target.value)}
                            placeholder="field1, field2, field3"
                            className="text-xs h-8"
                          />
                          <p className="text-[10px] text-muted-foreground">
                            Comma-separated field names
                          </p>
                        </div>

                        {/* Output Type */}
                        <div className="space-y-1">
                          <label className="text-xs font-semibold">
                            Output Type
                          </label>
                          <Select
                            value={outputType}
                            onValueChange={(value) => setOutputType(value)}
                          >
                            <SelectTrigger className="w-full text-xs h-8">
                              <SelectValue placeholder="Select output type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem
                                  value="keepMatches"
                                  className="text-xs"
                                >
                                  Keep Matches
                                </SelectItem>
                                <SelectItem
                                  value="keepNonMatches"
                                  className="text-xs"
                                >
                                  Keep Non-Matches
                                </SelectItem>
                                <SelectItem
                                  value="keepEverything"
                                  className="text-xs"
                                >
                                  Keep Everything
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Output Data From */}
                        <div className="space-y-1">
                          <label className="text-xs font-semibold">
                            Output Data From
                          </label>
                          <Select
                            value={outputDataFrom}
                            onValueChange={(value) => setOutputDataFrom(value)}
                          >
                            <SelectTrigger className="w-full text-xs h-8">
                              <SelectValue placeholder="Select data source" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="both" className="text-xs">
                                  Both Inputs
                                </SelectItem>
                                <SelectItem value="input1" className="text-xs">
                                  Input 1 Only
                                </SelectItem>
                                <SelectItem value="input2" className="text-xs">
                                  Input 2 Only
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Options Section */}
                        <div className="border-t pt-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <h4 className="text-xs font-semibold">Options</h4>
                            <div className="relative">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setShowOptionsMenu(!showOptionsMenu)
                                }
                                className="h-6 px-2 text-xs"
                              >
                                <Plus size={12} className="mr-1" /> Add Option
                              </Button>

                              {showOptionsMenu && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowOptionsMenu(false)}
                                  />
                                  <div className="absolute right-0 top-8 z-20 bg-background border shadow-lg rounded-md py-1 min-w-[180px]">
                                    {activeOptions.disableDotNotation ===
                                      undefined && (
                                      <div
                                        className="px-3 py-2 text-xs cursor-pointer hover:bg-accent transition"
                                        onClick={() =>
                                          addOption("disableDotNotation")
                                        }
                                      >
                                        Disable Dot Notation
                                      </div>
                                    )}
                                    {activeOptions.fuzzyCompare ===
                                      undefined && (
                                      <div
                                        className="px-3 py-2 text-xs cursor-pointer hover:bg-accent transition"
                                        onClick={() =>
                                          addOption("fuzzyCompare")
                                        }
                                      >
                                        Fuzzy Compare
                                      </div>
                                    )}
                                    {activeOptions.disableDotNotation !==
                                      undefined &&
                                      activeOptions.fuzzyCompare !==
                                        undefined && (
                                        <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                                          All options added
                                        </div>
                                      )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Active Options */}
                          {activeOptions.disableDotNotation !== undefined && (
                            <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                              <span className="text-xs font-medium">
                                Disable Dot Notation
                              </span>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={activeOptions.disableDotNotation}
                                  onCheckedChange={(checked) =>
                                    updateOption("disableDotNotation", checked)
                                  }
                                  className="scale-75"
                                />
                                <Trash2
                                  size={14}
                                  className="cursor-pointer text-primary hover:text-primary transition"
                                  onClick={() =>
                                    removeOption("disableDotNotation")
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {activeOptions.fuzzyCompare !== undefined && (
                            <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                              <span className="text-xs font-medium">
                                Fuzzy Compare
                              </span>
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={activeOptions.fuzzyCompare}
                                  onCheckedChange={(checked) =>
                                    updateOption("fuzzyCompare", checked)
                                  }
                                  className="scale-75"
                                />
                                <Trash2
                                  size={14}
                                  className="cursor-pointer text-red-500 hover:text-red-700 transition"
                                  onClick={() => removeOption("fuzzyCompare")}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* ↑ scroll wrapper closes here */}
                </div>
                {/* Execute button + info — outside scroll, pinned at bottom */}
                <div className="flex-shrink-0 pt-2 border-t bg-background flex flex-col gap-2">
                  {inputData && inputData.length > 0 && (
                    <div className="text-xs text-muted-foreground text-center p-2 bg-muted/20 rounded">
                      {inputData.length} input
                      {inputData.length !== 1 ? "s" : ""} connected
                    </div>
                  )}
                  <Button
                    onClick={executeNode}
                    disabled={
                      loading ||
                      !inputData ||
                      inputData.length === 0 ||
                      !isTitleValid ||
                      title.trim().length === 0
                    }
                    className="w-full"
                    size="sm"
                  >
                    <FlaskConical size={14} className="mr-2" />
                    {loading ? "Executing..." : "Execute step"}
                  </Button>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Output Preview (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute merge to see output preview"
                  loadingMessage="Merging data..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default MergeNode;
